import React, { useState, useMemo, useRef } from 'react'
import clsx from 'clsx'
import { LlamaCheckmark, LlamaMinus, LlamaAlertTriangle, LlamaCursorDown, LlamaInfoCircle } from '@easyllama/ui-components/icons'
import { PLAN_METADATA, COMPARISON_SECTIONS } from '~/service/Billing/billingConstants'
import { formatPrice } from '~/service/Billing/utils'
import { Tooltip, TooltipTrigger, TooltipContent } from '~/atoms/shadcn/tooltip'
import { useMediaQuery } from '~/hooks/useMediaQuery'

const HEADER_COL_PCT = 42

const INCLUSION_ICON_SIZE = 32

const inclusionIcon = (status) => {
  return status === true
    ? <LlamaCheckmark size={INCLUSION_ICON_SIZE} className='text-llama-purple-5 shrink-0' />
    : status === 'warning'
      ? <LlamaAlertTriangle size={INCLUSION_ICON_SIZE} className='text-llama-lemon-5 shrink-0' />
      : <LlamaMinus size={INCLUSION_ICON_SIZE} className='text-llama-grey-4 shrink-0' />
}

function useIsLgUp() {
  return useMediaQuery('(min-width: 1024px)')
}

function FeatureLabel({ feature, tapToOpen = false }) {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const isLgUp = useIsLgUp()
  const wasOpenRef = useRef(tooltipOpen)
  const pointerDownSeenRef = useRef(false)
  const tooltipProps = tapToOpen ? { open: tooltipOpen, onOpenChange: setTooltipOpen } : {}
  const iconProps = tapToOpen
    ? {
        onPointerDown: () => {
          wasOpenRef.current = tooltipOpen
          pointerDownSeenRef.current = true
        },
        onClick: (event) => {
          event.preventDefault()
          const wasOpen = pointerDownSeenRef.current ? wasOpenRef.current : tooltipOpen
          pointerDownSeenRef.current = false
          setTooltipOpen(!wasOpen)
        },
      }
    : {}

  return (
    <span className='text-sm text-left leading-snug whitespace-normal'>
      <span className='text-llama-grey-7'>{feature.featureText}</span>
      {feature.tooltip && (
        <Tooltip {...tooltipProps}>
          <TooltipTrigger asChild>
            <span className='inline-block shrink-0 align-middle ml-1 cursor-pointer' {...iconProps}>
              <LlamaInfoCircle size={16} className='text-llama-purple-4' />
            </span>
          </TooltipTrigger>
          <TooltipContent side={isLgUp ? 'right' : 'top'} className='max-w-64 max-lg:max-w-[calc(100vw-1rem)]' showArrow>
            {feature.tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}

function CollapsibleSections({ expanded, onToggle, titleClassName, renderFeatureContent }) {
  return (
    <>
      {COMPARISON_SECTIONS.map((section, sectionIndex) => {
        const isLastSection = sectionIndex === COMPARISON_SECTIONS.length - 1
        return (
          <div
            key={sectionIndex}
            className={clsx(
              sectionIndex > 0 && 'border-t border-llama-grey-8',
              isLastSection && 'border-b border-llama-grey-8',
            )}
          >
            <button
              type='button'
              className={clsx(
                'flex items-center gap-2 w-full py-4 text-left cursor-pointer max-md:min-h-20',
                expanded[sectionIndex] && 'border-b border-llama-grey-8',
              )}
              onClick={() => onToggle(sectionIndex)}
            >
              <LlamaCursorDown
                size={24}
                className={clsx(
                  'shrink-0 transition-transform duration-200 text-llama-grey-7',
                  !expanded[sectionIndex] && '-rotate-90',
                )}
              />
              <span className={clsx('font-bold text-llama-grey-7', titleClassName)}>{section.featureHeader}</span>
            </button>

            {expanded[sectionIndex] && section.features.map((feature, i) => {
              const isFirstFeature = i === 0
              const isLastFeature = i === section.features.length - 1
              return (
                <div
                  key={i}
                  className={clsx(
                    'pb-4 mb-4 border-b border-llama-grey-8',
                    isFirstFeature && 'pt-4',
                    isLastFeature && 'border-b-0 mb-0',
                  )}
                >
                  <div className='mb-3 text-left'><FeatureLabel feature={feature} tapToOpen /></div>
                  {renderFeatureContent(feature)}
                </div>
              )
            })}
          </div>
        )
      })}
    </>
  )
}

export default function BillingComparisonTable({ plans = [], priceOverrides = {} }) {
  // Not `hidden {breakpoint}:block` - see useMediaQuery for why.
  const isLgUp = useMediaQuery('(min-width: 1024px)')
  const isMdUp = useMediaQuery('(min-width: 768px)')

  const { planColumns, headerColWidth, planColWidth } = useMemo(() => {
    const cols = plans
      .filter((p) => !PLAN_METADATA[p.unit]?.legacyMessage)
      .map((p) => {
        const meta = PLAN_METADATA[p.unit] ?? {}
        const periodSuffix = `${meta.periodUnit ?? ''}${meta.periodCycle ?? ''}`
        const defaultPrice = meta.priceLabel ?? `${formatPrice(p.cost_per_seat)}${periodSuffix}`
        const overrideBase = priceOverrides[p.unit]
        const priceAmount = overrideBase || meta.priceLabel || formatPrice(p.cost_per_seat)
        const priceSuffix = !overrideBase && meta.priceLabel ? '' : periodSuffix
        return {
          id: p.unit,
          name: p.name,
          price: overrideBase ? `${overrideBase}${periodSuffix}` : defaultPrice,
          priceAmount,
          priceSuffix,
        }
      })
    const planPct = cols.length > 0 ? (100 - HEADER_COL_PCT) / cols.length : 20
    return {
      planColumns: cols,
      headerColWidth: `${HEADER_COL_PCT}%`,
      planColWidth: `${planPct}%`,
    }
  }, [plans, priceOverrides])

  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(COMPARISON_SECTIONS.map((_, i) => [i, true]))
  )

  const gridCols = `${headerColWidth} ${planColumns.map(() => planColWidth).join(' ')}`

  const toggleSection = (i) =>
    setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))

  return (
    <div className='pb-10 px-10 lining-nums'>
      <h3 className='mb-6 text-center font-[RecoletaSemiBold] text-2xl font-semibold text-llama-grey-7'>
        Feature comparison details
      </h3>
      <div className='w-full' style={{ display: isLgUp ? 'block' : 'none' }} data-testid='comparison-table-desktop'>
        {COMPARISON_SECTIONS.map((section, sectionIndex) => (
          <div key={sectionIndex}>

            {/* Plan column headers — rendered only above the first section */}
            {sectionIndex === 0 && (
              <div
                className='grid h-20 items-center bg-white'
                style={{ gridTemplateColumns: gridCols }}
              >
                <div />
                {planColumns.map((col) => (
                  <div key={col.id}>
                    <div className='font-bold text-sm text-llama-grey-7 leading-normal'>{col.name}</div>
                    <div className='text-xs text-gray-500 mt-1 font-normal'>{col.price}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Section header row (collapsible) */}
            <div
              className='grid h-20 items-center cursor-pointer bg-white border-t border-llama-grey-8 hover:bg-gray-50/50'
              style={{ gridTemplateColumns: gridCols }}
              onClick={() => toggleSection(sectionIndex)}
            >
              <div className='flex items-center gap-2 pr-3'>
                <LlamaCursorDown
                  size={24}
                  className={clsx('shrink-0 transition-transform duration-200 text-llama-grey-7', !expanded[sectionIndex] && '-rotate-90')}
                />
                <span className='font-bold text-base text-llama-grey-7 whitespace-normal text-left'>
                  {section.featureHeader}
                </span>
              </div>
              {planColumns.map((col) => <div key={col.id} />)}
            </div>

            {/* Feature rows */}
            {expanded[sectionIndex] && section.features.map((feature, i) => {
              const isLastRow = sectionIndex === COMPARISON_SECTIONS.length - 1 && i === section.features.length - 1
              return (
              <div
                key={i}
                className={clsx('grid h-20 items-center border-t border-llama-grey-8 bg-white hover:bg-gray-50/50', isLastRow && 'border-b')}
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className='pl-8.5 pr-3 text-left'><FeatureLabel feature={feature} /></div>
                {planColumns.map((col) => (
                  <div key={col.id} className='flex items-center justify-center'>
                    {inclusionIcon(feature.included[col.id])}
                  </div>
                ))}
              </div>
              )
            })}

          </div>
        ))}
      </div>

      <div className='w-full' style={{ display: isMdUp && !isLgUp ? 'block' : 'none' }} data-testid='comparison-table-tablet'>
        <div
          className='grid gap-4 pb-4 mb-2 border-b border-llama-grey-8'
          style={{ gridTemplateColumns: `repeat(${planColumns.length}, 1fr)` }}
        >
          {planColumns.map((col) => (
            <div key={col.id}>
              <div className='font-bold text-sm text-llama-grey-7 leading-normal'>{col.name}</div>
              <div className='text-xs text-gray-500 mt-1 font-normal'>{col.price}</div>
            </div>
          ))}
        </div>

        <CollapsibleSections
          expanded={expanded}
          onToggle={toggleSection}
          titleClassName='text-base'
          renderFeatureContent={(feature) => (
            <div className='grid gap-4' style={{ gridTemplateColumns: `repeat(${planColumns.length}, 1fr)` }}>
              {planColumns.map((col) => (
                <div key={col.id} className='flex items-center justify-center'>
                  {inclusionIcon(feature.included[col.id])}
                </div>
              ))}
            </div>
          )}
        />
      </div>

      <div className='w-full' style={{ display: isMdUp ? 'none' : 'block' }} data-testid='comparison-table-mobile'>
        <div className='flex items-start justify-between gap-3 pb-4 mb-2 border-b border-llama-grey-8' data-testid='comparison-table-mobile-legend'>
          {planColumns.map((col) => (
            <div key={col.id} className='flex-1 text-left min-w-0'>
              <div className='font-bold text-sm text-llama-grey-7 leading-snug'>{col.name}</div>
              <div className='text-sm text-gray-500 mt-1 font-normal leading-snug'>
                <div>{col.priceAmount}</div>
                {col.priceSuffix && <div>{col.priceSuffix}</div>}
              </div>
            </div>
          ))}
        </div>

        <CollapsibleSections
          expanded={expanded}
          onToggle={toggleSection}
          titleClassName='text-sm'
          renderFeatureContent={(feature) => (
            <div className='space-y-3'>
              {planColumns.map((col) => (
                <div key={col.id} className='flex items-center gap-2 text-sm'>
                  {inclusionIcon(feature.included[col.id])}
                  <span className='text-llama-grey-5'>{col.name}</span>
                </div>
              ))}
            </div>
          )}
        />
      </div>
    </div>
  )
}
